import { ThemeProvider } from "@material-tailwind/react";
import _ from 'lodash';
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import EditOrInviteUserModal from "../components/Modals/EditOrInviteUser";
import NotificationSettings from "../components/NotificationSettings";
import UserAndInviteList from "../components/UserAndInviteList";
import UserCard from "../components/UserCard";
import { getTokenFromLocalStorage } from "../lib/local-storage";
import { SocketRPC, socketEmit } from "../lib/socket";
import useUser from "../store/user";

const theme = {
    input: {
      styles: {
        base: {
          container: {
            minWidth: "min-w-[50px]",
            alignItems: "center",
          },
        },
      },
    },
  };

const addRoleName = (userOrInvite, fullSetOfRoles) => {
    const roleNames = userOrInvite.roleKeys.map((roleKey) => {
        const role = fullSetOfRoles.find((role) => role.roleKey === roleKey);
        return role?.roleName;
    });
    return {
        ...userOrInvite,
        roleNames,
    };
};

const addRoleNames = (usersOrInvites, fullSetOfRoles) => {
    return usersOrInvites.map((userOrInvite) => addRoleName(userOrInvite, fullSetOfRoles));
};

const SettingsPage = () => {
    const { user } = useUser();

    const [superAdminUsers, setSuperAdminUsers] = useState([]);
    const [superAdminInvites, setSuperAdminInvites] = useState<any[]>([]); // TODO type
    const [superAdminRoles, setSuperAdminRoles] = useState([]); // TODO type
    const [selectedSuperAdminRoles, setSelectedSuperAdminRoles] = useState({}); // TODO type
    const [selectedUser, setSelectedUser] = useState({}); // TODO type
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [token, setToken] = useState<string | null>(null);
    const { permissionKeys, producerKey, orderSourceKey, partnerKey, userName } = user || {};

    const canManageUsers = permissionKeys?.includes('SUPER_ADMIN_CAN_MANAGE_USERS');
    const canInviteUsers = permissionKeys?.includes('SUPER_ADMIN_CAN_SEND_INVITE');
    const canViewInvites = permissionKeys?.includes('SUPER_ADMIN_CAN_VIEW_INVITES');

    const getSelectedRoles = (superAdminRoles, user) => {
        let selectedRoles = {};
        const {roleKeys = []} = user;
        superAdminRoles.forEach((role) => {
            selectedRoles[role.roleKey] = roleKeys.includes(role.roleKey);
        });
        return selectedRoles;
    };

    const refreshUsers = () => {
        const token = getTokenFromLocalStorage();

        return socketEmit(SocketRPC.GetSuperAdminRoles, {user})
            .then((res) => {
                if (!res.success) {
                    console.log('GetSuperAdminRoles failed', res.error);
                }
                setSuperAdminRoles(res.roles)
                return res.roles;
            })
            .then((fullSetOfRoles) => {
                if (canManageUsers) {
                    socketEmit(SocketRPC.GetSuperAdminUsers, {user, producerKey, orderSourceKey, partnerKey})
                        .then((res) => {
                            if (!res.success) {
                                console.log('GetSuperAdminUsers failed', res.error);
                            }
                            const usersExcludingSelf = res.users?.filter((u) => u.userName !== userName);
                            const supplementedUsers = addRoleNames(usersExcludingSelf, fullSetOfRoles);
                            setSuperAdminUsers(supplementedUsers);
                        });
                }
                return fullSetOfRoles;
            })
            .then((fullSetOfRoles) => {
                if (canViewInvites) {
                    socketEmit(SocketRPC.GetSuperAdminInvites, {token})
                        .then((res) => {
                            if (!res.success) {
                                console.log('GetSuperAdminInvites failed', res.error);
                            }

                            const openInvites = res.invites?.filter((invite) => ['sent', 'viewed', 'reminded'].includes(invite.state));
                            const supplementedInvites = addRoleNames(openInvites, fullSetOfRoles);
                            setSuperAdminInvites(supplementedInvites);
                        });
                }
            })
            .catch(err => {
                console.error(err);
            });
    };

    const toggleOpen = () => {
        if (editModalOpen) {
            setSelectedUser({});
        }
        setEditModalOpen(!editModalOpen)
    };

    useEffect(() => {
        const token = getTokenFromLocalStorage();
        setToken(token);
        refreshUsers();
    }, [user]);


    useEffect(() => {
        if (!_.isEmpty(selectedUser)) {
            setSelectedSuperAdminRoles(getSelectedRoles(superAdminRoles, selectedUser));
            toggleOpen();
        };
    }, [selectedUser]);


    const updateUserRoles = async (updatedUser) => {
        return socketEmit(SocketRPC.UpdateSuperAdminUserRoles, {requestUser: user, editUser: updatedUser})
    };

    const inviteUser = async (inviteUser) => {
        return socketEmit(SocketRPC.InviteSuperAdminUser, {token, inviteUser})
    };

    const deleteUser = async (userToBeDeleted) => {
        return socketEmit(SocketRPC.DeleteSuperAdminUser, {token, userToBeDeleted})
            .then((res) => {
                if (!res.success) {
                    throw new Error(res.error);
                }
                toast.success(`Successfully deleted user`);
            })
            .catch((err) => {
                console.error(err);
                toast.error(
                    `Failed to delete user. Please try again`
                );
            })
            .finally(() => {
                refreshUsers();
            });
    };

    const bumpInvite = async (invite) => {
        return socketEmit(SocketRPC.BumpSuperAdminInvite, {token, invite})
            .then((res) => {
                if (!res.success) {
                    throw new Error(res.error);
                }
                const {invite} = res;
                const supplementedInvite = addRoleName(invite, superAdminRoles);
                const updatedInvites = superAdminInvites.filter((i) => i.inviteKey !== invite.inviteKey);
                updatedInvites.push(supplementedInvite);
                setSuperAdminInvites(updatedInvites);
                toast.success(`Successfully bumped invite`);
            })
            .catch((err) => {
                console.error(err);
                toast.error(`Failed to bump invite. Please try again`);
            });
    };

    const cancelInvite = async (invite) => {
        return socketEmit(SocketRPC.CancelSuperAdminInvite, {token, invite})
            .then((res) => {
                if (!res.success) {
                    throw new Error(res.error);
                }
                const updatedInvites = superAdminInvites.filter((i) => i.inviteKey !== invite.inviteKey);
                setSuperAdminInvites(updatedInvites);
                toast.success(`Successfully deleted invite`);
            })
            .catch((err) => {
                console.error(err);
                toast.error(`Failed to delete invite. Please try again`);
            });
    };

    return (
		<ThemeProvider value={theme}>
			<div className="flex justify-between items-center m-3 h-14">
				<h5 className="text-xl font-semibold">Settings</h5>
			</div>
            {user &&
                <UserCard user={user} superAdminRoles={superAdminRoles} />
            }
            <NotificationSettings />
			{canManageUsers && (
                <UserAndInviteList
                    users={superAdminUsers}
                    invites={superAdminInvites}
                    setSelectedUser={setSelectedUser}
                    onDeleteUser={deleteUser}
                    onBumpInvite={bumpInvite}
                    onDeleteInvite={cancelInvite}
                    canInviteUsers={canInviteUsers}
                />
            )}
			<EditOrInviteUserModal
				user={selectedUser}
				superAdminRoles={superAdminRoles}
				selectedSuperAdminRoles={selectedSuperAdminRoles}
				setSelectedSuperAdminRoles={setSelectedSuperAdminRoles}
				updateUserRoles={updateUserRoles}
                inviteUser={inviteUser}
				editModalOpen={editModalOpen}
				toggleOpen={toggleOpen}
				refreshUsers={refreshUsers}
			/>
		</ThemeProvider>
	);
};

export default SettingsPage;
